import { Box, Stack } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import { PriceCategoryListSortColumn, SortOrder } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { usePriceCategoriesListQuery } from "PriceCategories/Api/Queries/usePriceCategoriesListQuery";
import {
  PRICE_CATEGORIES_SORTABLE_COLUMNS,
  usePriceCategoriesTableColumns,
} from "PriceCategories/Components/usePriceCategoriesTableColumns";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";

const SORT_COLUMN_MAP: Record<string, PriceCategoryListSortColumn> = {
  name: PriceCategoryListSortColumn.Name,
  type: PriceCategoryListSortColumn.Type,
  dateCreated: PriceCategoryListSortColumn.DateCreated,
  dateUpdated: PriceCategoryListSortColumn.DateUpdated,
};

export const PriceCategoriesTable = (): React.ReactNode => {
  const { t } = useResource();

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    sorting,
    onSortingChange,
  } = useTableSearchParams({
    validSortIds: [...PRICE_CATEGORIES_SORTABLE_COLUMNS],
  });

  const sortBy = sorting[0]?.id ? SORT_COLUMN_MAP[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data: categoriesData, isLoading } = usePriceCategoriesListQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    searchQuery: debouncedSearch || undefined,
    sortBy,
    sortOrder,
  });

  const categories = categoriesData?.items ?? [];
  const totalCount = categoriesData?.totalCount ?? 0;

  const columns = usePriceCategoriesTableColumns();

  const table = useReactTable({
    data: categories,
    columns,
    getRowId: (row) => row.priceCategoryID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize }, sorting },
    onSortingChange,
    onPaginationChange,
  });

  return (
    <Box>
      <Stack sx={{ marginBottom: Spacing.M }}>
        <Box sx={{ width: "100%" }}>
          <TableSearchInput
            name="price-category-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.PriceCategories.SearchPlaceholder)}
          />
        </Box>
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
      />
    </Box>
  );
};
